import { bestSsnMatch, formatDate, formatPhone, maskedSsn, titleCase } from '../BusinessDetailReport/businessReportModel'
import { moreFromLexis } from './moreFromLexis'

const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])
const text = (value) => (value == null || typeof value === 'object' ? '' : String(value)).trim()
const upper = (value) => text(value).toUpperCase()
const dateValue = (date) => (date && typeof date === 'object' && date.Year ? new Date(date.Year, (date.Month || 1) - 1, date.Day || 1).getTime() : 0)
const yes = (value) => /^(Y|YES|TRUE|1)$/.test(upper(value))

const CHAPTERS = { 7: 'liquidation', 11: 'reorganization', 12: 'family farmer', 13: 'repayment plan' }

export const outcomeOf = (bankruptcy) => {
    const value = upper(bankruptcy?.Disposition || list(bankruptcy?.Debtors?.Debtor).map(d => d?.DispositionTypeDesc).find(Boolean))
    const reopened = dateValue(bankruptcy?.ReopenDate)
    if (/REOPEN/.test(value) || (reopened && reopened > Math.max(dateValue(bankruptcy?.ClosedDate), dateValue(bankruptcy?.DischargeDate)))) return { label: 'Open', tone: 'declined' }
    if (/DISMISS/.test(value)) return { label: 'Dismissed', tone: 'watch' }
    if (/DISCHARG/.test(value)) return { label: 'Discharged', tone: 'neutral' }
    if (/CLOSE|TERMINAT/.test(value) || dateValue(bankruptcy?.ClosedDate)) return { label: 'Closed', tone: 'neutral' }
    return { label: 'Open', tone: 'declined' }
}

const nameCase = (value) => titleCase(value).replace(/(^|\s)([a-z])(?=\s|$)/g, (m, space, letter) => space + letter.toUpperCase())
const personName = (n) => nameCase(n?.CompanyName || [n?.First, n?.Middle, n?.Last, n?.Suffix].filter(Boolean).join(' ') || n?.OriginalName)
const contact = (people) => list(people).map(p => {
    const names = list(p?.Names?.Name)
    const name = names.map(n => nameCase([n?.First, n?.Middle, n?.Last, n?.Suffix].filter(Boolean).join(' ') || n?.OriginalName)).find(Boolean) || ''
    const firm = names.map(n => titleCase(n?.CompanyName)).find(Boolean)
    const phone = list(p?.Phones?.Phone).map(ph => formatPhone(ph?.Phone10)).find(Boolean)
    return [name, firm && firm !== name && firm, phone].filter(Boolean).join(' · ')
}).filter(Boolean).join('; ')

export const buildBankruptcy = (b, owner = {}, now = new Date()) => {
    const chapter = text(b?.Chapter)
    const original = text(b?.OriginalChapter)
    const converted = original && chapter && original !== chapter
    const outcome = outcomeOf(b)
    const joint = /JOINT/.test(upper(b?.FilerType)) || list(b?.Debtors?.Debtor).length > 1
    const filed = b?.FilingDate || b?.OriginalFilingDate
    const years = dateValue(filed) ? (now.getTime() - dateValue(filed)) / (365.25 * 86400000) : null
    const recent = years != null && years <= 7
    const debtors = list(b?.Debtors?.Debtor).filter(Boolean)
    const ssnMatch = bestSsnMatch(debtors.map(d => d?.SSN || d?.AppendedSSN), owner?.SSN)
    const ended = b?.DischargeDate && outcome.label === 'Discharged' ? b.DischargeDate : b?.ClosedDate
    const history = list(b?.StatusHistory?.Status).filter(s => s?.Type)
        .sort((x, y) => dateValue(x?.Date) - dateValue(y?.Date))
    const court = [titleCase(b?.CourtName), titleCase(b?.CourtLocation)].filter((v, i, all) => v && all.indexOf(v) === i)

    return {
        more: moreFromLexis(b, 'Bankruptcies/Bankruptcy[]'),
        filedAt: dateValue(filed), recent, open: outcome.label === 'Open', outcome: outcome.label, chapter,
        title: [chapter ? `Chapter ${chapter}` : 'Bankruptcy', joint ? 'Joint' : 'Individual'].join(' · '),
        facts: [filed && `Filed ${formatDate(filed)}`, ended && `${outcome.label === 'Discharged' ? 'Discharged' : 'Ended'} ${formatDate(ended)}`,
            court[0] || b?.FilingJurisdiction].filter(Boolean).join(' · '),
        chips: [
            { label: outcome.label, tone: outcome.tone },
            recent && { label: 'Filed in the last 7 years', tone: 'declined' },
            converted && { label: `Converted from chapter ${original}`, tone: 'watch' },
            ssnMatch === 'different' && { label: 'SSN differs', tone: 'watch' },
        ].filter(Boolean),
        details: [
            ['Same person?', ssnMatch === 'same' ? "A debtor's SSN matches the owner" : ssnMatch === 'prefix' ? "A debtor's SSN starts with the same 5 digits as the owner's"
                : ssnMatch === 'different' ? "No debtor's SSN matches the owner"
                : 'Lexis matched this case by name; no SSN to compare'],
            ['Chapter', chapter && `${chapter}${CHAPTERS[chapter] ? ` (${CHAPTERS[chapter]})` : ''}${converted ? `, converted from chapter ${original}${b?.ConvertedDate ? ` on ${formatDate(b.ConvertedDate)}` : ''}` : ''}`],
            ['Outcome', [titleCase(b?.Disposition) || outcome.label, debtors.map(d => titleCase(d?.DispositionReason)).find(Boolean)].filter(Boolean).join(' · ')],
            ['How it was dismissed', titleCase(b?.MethodDismiss)],
            ['Filed', formatDate(filed)],
            ['Original filing', b?.OriginalFilingDate && dateValue(b.OriginalFilingDate) !== dateValue(filed) ? formatDate(b.OriginalFilingDate) : ''],
            ['Discharged', outcome.label === 'Discharged' ? formatDate(b?.DischargeDate) : ''],
            ['Closed', formatDate(b?.ClosedDate)],
            ['Reopened', formatDate(b?.ReopenDate)],
            ['Filer', [titleCase(b?.FilerType), yes(b?.CorpFlag) && 'business filing'].filter(Boolean).join(', ')],
            ['Debtors', debtors.map(d => [list(d?.Names?.Name).map(personName).find(Boolean),
                maskedSsn(d?.SSN || d?.AppendedSSN) && `SSN ${maskedSsn(d.SSN || d.AppendedSSN)}`,
                text(d?.SSNMatchDesc) && `Lexis: ${text(d.SSNMatchDesc).toLowerCase().replace(/\bssn\b/g, 'SSN')}`,
                titleCase(d?.DispositionTypeDesc)].filter(Boolean).join(' · ')).filter(Boolean).join('; ')],
            ['No assets for unsecured creditors', upper(b?.AssetsForUnsecured) === 'N' ? 'Yes' : ''],
            ['Filed without a lawyer', yes(b?.SelfRepresented) ? 'Yes' : ''],
            ['Filed in error', yes(b?.FiledInError) ? 'Yes' : ''],
            ['Assets', text(b?.Assets) && Number(text(b.Assets).replace(/[^0-9.]/g, '')) > 0 ? `$${Number(text(b.Assets).replace(/[^0-9.]/g, '')).toLocaleString('en-US')}` : ''],
            ['Liabilities', text(b?.Liabilities) && Number(text(b.Liabilities).replace(/[^0-9.]/g, '')) > 0 ? `$${Number(text(b.Liabilities).replace(/[^0-9.]/g, '')).toLocaleString('en-US')}` : ''],
            ['Court', court.join(', ')],
            ['Judge', nameCase(b?.JudgeName)],
            ['Case number', text(b?.CaseNumber)],
            ["Debtor's attorney", contact(b?.Attorneys?.Attorney)],
            ['Trustee', contact(b?.Trustees?.Trustee)],
        ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        blocks: [
            history.length > 0 && {
                title: 'Status history',
                details: history.map(s => ({ label: formatDate(s.Date) || 'No date', value: titleCase(s.Type) })),
            },
            list(b?.Comments?.Comment).some(c => c?.Description) && {
                title: 'Court notes',
                details: list(b.Comments.Comment).filter(c => c?.Description)
                    .sort((x, y) => dateValue(x?.FilingDate) - dateValue(y?.FilingDate))
                    .map(c => ({ label: formatDate(c.FilingDate) || 'No date', value: titleCase(c.Description) })),
            },
        ].filter(Boolean),
    }
}

export function buildBankruptcies(bankruptcies, owner = {}, now = new Date()) {
    const rows = list(bankruptcies).filter(b => b && typeof b === 'object').map(b => buildBankruptcy(b, owner, now))
        .sort((a, b) => b.filedAt - a.filedAt)
    const latest = rows[0]
    return {
        rows,
        summary: {
            count: rows.length,
            latest: latest ? { year: latest.filedAt ? new Date(latest.filedAt).getFullYear() : null, chapter: latest.chapter, outcome: latest.outcome } : null,
            recent: rows.filter(r => r.recent).length,
            open: rows.filter(r => r.open).length,
        },
    }
}
